import { useId, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Camera,
  FileText,
  Info,
  Link2,
  Mail,
  Scale,
  ShieldCheck,
  Trophy,
} from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Breadcrumbs } from '@/components/ui/Breadcrumbs';
import { Input, Select, Textarea } from '@/components/ui/Input';
import { Skeleton } from '@/components/ui/Skeleton';
import { toast } from '@/components/ui/Toast';
import { Collapsible } from '@/components/ui/Collapsible';
import { Switch } from '@/components/forms/Switch';
import { brandLegalApi, brandsApi } from '@/features/brands/api';
import { AcknowledgmentDialog } from '@/features/legal/AcknowledgmentDialog';
import { legalApi, type LegalAcceptance } from '@/features/legal/api';
import { HelpHint } from '@/features/help/HelpHint';
import { useFavoritable } from '@/features/favorites/FavoritableContext';
import { ApiError } from '@/lib/api/errors';
import { queryKeys } from '@/lib/api/queryKeys';
import type { CompanyLegalPatch, XkBrand } from '@/lib/api/types';
import { cn } from '@/lib/cn';
import { useT } from '@/lib/i18n/useT';
import { useCanAccess } from '@/lib/permissions/useCanAccess';
import { usePrefsStore } from '@/lib/prefs/usePrefsStore';

/**
 * Settings › Legal & Consent — la página legal de UNA marca.
 *
 * Es la única fuente de los instrumentos legales que el invitado ve en el paso
 * de consentimiento de cualquier encuesta desplegada por esa marca, en Xotski,
 * Xamplin, Xurvey o DiD: términos, privacidad, Official Rules, consentimiento
 * de imagen, age gate y canales de marketing. Xotski es una VISTA de la fila
 * (`co_company_legal`, `GET|PUT /co-companies/:id/legal`), no su dueño.
 *
 * Imagen, SMS y Standard Tier van en un panel plegado («Otras aplicaciones y
 * eventos anteriores»): se pliega, no se quita. Esta página es el único editor
 * de `co_company_legal` y las demás apps y los eventos anteriores los leen.
 *
 * Qué NO se edita aquí: el aviso en pantalla y las etiquetas de las casillas.
 * Esos textos son siempre de Onxite, versionados, porque son lo que hace
 * probable el consentimiento — una marca no puede reescribirlos.
 *
 * La marca es la Responsable (controller) de los datos del invitado y Onxite
 * procesa por cuenta de ella; por eso todo se guarda POR MARCA, nunca por
 * agencia. Cambiar de marca suelta el borrador no guardado: con textos
 * legales, guardar en la marca equivocada es el peor error de esta pantalla.
 */
export function LegalSettingsPage() {
  const t = useT();
  useFavoritable(t('settings.legal.title'));
  const puedeEditar = useCanAccess('CoCompany', 'edit');

  const marcas = useQuery({ queryKey: queryKeys.brands.all, queryFn: () => brandsApi.list() });
  const lista: XkBrand[] = marcas.data?.items ?? [];

  const [elegida, setElegida] = useState<number | null>(null);
  const companyId = elegida ?? lista[0]?.id ?? 0;

  return (
    <div className="page space-y-6">
      <Breadcrumbs
        items={[{ label: t('breadcrumbs.settings'), to: '/settings' }, { label: t('settings.legal.title') }]}
      />

      <header>
        <h1 className="flex items-center gap-2 text-2xl font-bold text-[var(--color-fg-primary)]">
          <Scale className="h-5 w-5" />
          {t('settings.legal.title')}
          <HelpHint section="settings-legal" />
        </h1>
        <p className="mt-1 max-w-3xl text-sm text-[var(--color-fg-muted)]">{t('settings.legal.subtitle')}</p>
      </header>

      <Card className="p-5">
        <div className="flex flex-wrap items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-[var(--color-accent-ink)]" />
          <h2 className="text-base font-semibold text-[var(--color-fg-primary)]">
            {t('settings.legal.roles.title')}
          </h2>
          <Badge tone="neutral">{t('settings.legal.roles.brand')}</Badge>
          <Badge tone="neutral">{t('settings.legal.roles.app')}</Badge>
        </div>
        <p className="mt-2 text-sm text-[var(--color-fg-muted)]">{t('settings.legal.roles.description')}</p>

        {marcas.isLoading ? (
          <Skeleton className="mt-4 h-10 w-full max-w-sm" />
        ) : lista.length === 0 ? (
          <p className="mt-4 text-sm text-[var(--color-fg-muted)]">{t('settings.brand.noBrands')}</p>
        ) : (
          <label className="mt-4 block">
            <span className="text-[11px] font-extrabold uppercase tracking-widest text-[var(--color-fg-muted)]">
              {t('settings.terms.brand')}
            </span>
            <Select
              value={companyId}
              onChange={(e) => setElegida(Number(e.target.value))}
              className="mt-1 max-w-sm"
              disabled={lista.length === 1}
            >
              {lista.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </Select>
            <span className="mt-1 block text-xs text-[var(--color-fg-muted)]">{t('settings.legal.brandHint')}</span>
          </label>
        )}
      </Card>

      {companyId > 0 ? (
        <LegalForm key={companyId} companyId={companyId} puedeEditar={puedeEditar} />
      ) : null}
    </div>
  );
}

/** Los campos editables de la página (sin el `acknowledgmentId`, que no es un campo). */
type CampoLegal = Exclude<keyof CompanyLegalPatch, 'acknowledgmentId'>;
type Borrador = Partial<Record<CampoLegal, string | number | boolean | null>>;

/*
 * Tope de cada texto legal pegado: el del servidor (`LEGAL_TEXT_MAX_LENGTH`,
 * ox-platform/src/apps/console/dto/co-company-legal.dto.ts, S-1). Si cambia
 * allí, se cambia aquí. El servidor cuenta puntos de código y aquí se cuenta
 * `length` (UTF-16): igual o más estricto, nunca deja pasar lo que él rechaza.
 * Desde el 90 % el contador enseña «n / tope».
 */
const LEGAL_TEXT_MAX_LENGTH = 100_000;
const LEGAL_TEXT_WARN_AT = 90_000;

/** Los textos pegados que llevan el tope, con la etiqueta que los nombra en el aviso. */
const TEXTOS_LEGALES = [
  ['termsText', 'settings.legal.terms.title'],
  ['officialRulesText', 'settings.legal.rules.title'],
  ['mediaConsentText', 'settings.legal.media.title'],
] as const satisfies ReadonlyArray<readonly [CampoLegal, string]>;

/**
 * Los demás campos que pinta la página, con su etiqueta visible: un 400 de uno
 * de ellos se cuenta con el nombre que ve la marca, nunca con la frase del
 * servidor ni la clave camelCase.
 */
const CAMPOS_DE_LA_PAGINA = [
  ['termsUrl', 'settings.legal.terms.title', 'url'],
  ['privacyUrl', 'settings.legal.privacy.title', 'url'],
  ['officialRulesUrl', 'settings.legal.rules.title', 'url'],
  ['legalName', 'settings.legal.legalName.title', 'otro'],
  ['ageGateMinAge', 'settings.legal.ageGate.minAge', 'otro'],
  ['ageGateEnabled', 'settings.legal.ageGate.title', 'otro'],
  ['emailMarketingEnabled', 'settings.legal.marketing.emailLabel', 'otro'],
  ['smsMarketingEnabled', 'settings.legal.marketing.smsLabel', 'otro'],
  ['standardTier', 'settings.legal.standard.title', 'otro'],
  ['mediaAdoptGeneric', 'settings.legal.media.adoptLabel', 'otro'],
] as const satisfies ReadonlyArray<readonly [CampoLegal, string, 'url' | 'otro']>;

/**
 * La descripción del toast cuando falla el guardado. Con un 400 de validación
 * de un campo de la página, nombra el campo; si no, lo de siempre
 * (`e.message`). `ApiError.message` no se toca: lo leen todas las pantallas.
 */
function detalleDelError(
  e: unknown,
  t: ReturnType<typeof useT>,
  formato: (n: number) => string,
): string | undefined {
  if (e instanceof ApiError && e.fields) {
    const fields = e.fields;
    const texto = TEXTOS_LEGALES.find(([k]) => Object.prototype.hasOwnProperty.call(fields, k));
    if (texto) {
      return t('settings.legal.textLimit.toast', { field: t(texto[1]), max: formato(LEGAL_TEXT_MAX_LENGTH) });
    }
    for (const clave of Object.keys(fields)) {
      const campo = CAMPOS_DE_LA_PAGINA.find(([k]) => k === clave);
      if (campo) {
        return t(campo[2] === 'url' ? 'settings.legal.fieldError.url' : 'settings.legal.fieldError.generic', {
          field: t(campo[1]),
        });
      }
    }
  }
  return e instanceof Error ? e.message : undefined;
}

/**
 * El formulario de una marca. `key={companyId}` en el padre lo remonta al
 * cambiar de marca, y con él se va el borrador: no hay forma de guardar en la
 * marca que no era.
 */
function LegalForm({ companyId, puedeEditar }: { companyId: number; puedeEditar: boolean }) {
  const t = useT();
  const language = usePrefsStore((s) => s.language);
  const formato = (n: number) => new Intl.NumberFormat(language).format(n);
  const qc = useQueryClient();
  const legal = useQuery({
    queryKey: queryKeys.brands.legal(companyId),
    queryFn: () => brandLegalApi.get(companyId),
  });

  // `borrador` sólo guarda lo tocado; lo demás se pinta de la respuesta.
  const [borrador, setBorrador] = useState<Borrador>({});
  const sucio = Object.keys(borrador).length > 0;
  // Sólo lo tocado: un texto ya guardado que pasa del tope no se reenvía
  // (`patchPendiente`), así que no impide guardar lo demás.
  const excede = TEXTOS_LEGALES.some(([k]) => {
    const v = borrador[k];
    return typeof v === 'string' && v.length > LEGAL_TEXT_MAX_LENGTH;
  });
  const datos = legal.data;

  const valor = (k: CampoLegal): string | number | boolean | null => {
    if (k in borrador) return borrador[k] ?? null;
    return datos ? (datos[k] as string | number | boolean | null) : null;
  };
  const texto = (k: CampoLegal) => (valor(k) as string | null) ?? '';
  const bool = (k: CampoLegal) => valor(k) === true;
  const tocar = (k: CampoLegal, v: string | number | boolean | null) =>
    setBorrador((b) => ({ ...b, [k]: v }));

  // Vacío se guarda como NULL: «sin rellenar» y «cadena vacía» tienen que
  // ser el mismo estado, o la validación de premio no sabría cuál mirar.
  const patchPendiente = (): CompanyLegalPatch => {
    const patch: CompanyLegalPatch = {};
    for (const [k, v] of Object.entries(borrador) as Array<[CampoLegal, unknown]>) {
      (patch as Record<string, unknown>)[k] = typeof v === 'string' && v.trim() === '' ? null : v;
    }
    return patch;
  };

  /*
   * Guardar pasa por el Legal Setup Acknowledgment (Exhibit F, p. 1): el
   * representante confirma autoridad, rol de controller y garantía de sus
   * documentos, y esa firma es lo que el backend exige (428 sin ella). El
   * diálogo escribe la fila y devuelve su id; con él se guarda.
   */
  const [firmando, setFirmando] = useState(false);
  // El panel «Otras aplicaciones y eventos anteriores». LegalForm se remonta por marca: vuelve cerrado.
  const [otrasAbiertas, setOtrasAbiertas] = useState(false);
  const guardar = useMutation({
    mutationFn: (ack: LegalAcceptance) =>
      brandLegalApi.update(companyId, { ...patchPendiente(), acknowledgmentId: ack.id }),
    onSuccess: (fila) => {
      qc.setQueryData(queryKeys.brands.legal(companyId), fila);
      qc.invalidateQueries({ queryKey: queryKeys.brands.detail(companyId) });
      qc.invalidateQueries({ queryKey: ['legal', 'brand', companyId] });
      setBorrador({});
      setFirmando(false);
      toast({ tone: 'success', title: t('settings.legal.savedToast') });
    },
    onError: (e: unknown) =>
      toast({
        tone: 'error',
        title: t('settings.brand.errorToast'),
        description: detalleDelError(e, t, formato),
      }),
  });

  if (legal.isLoading) return <Skeleton className="h-64 w-full" />;
  if (legal.isError || !datos) {
    return (
      <Card className="p-5 text-sm text-[var(--color-fg-muted)]">
        {legal.error instanceof Error ? legal.error.message : t('common.error')}
      </Card>
    );
  }

  const ageGate = bool('ageGateEnabled');
  const edad = (valor('ageGateMinAge') as number | null) ?? 21;

  // La foto de lo que se va a reconocer: lo guardado más lo tocado.
  const siguiente = { ...datos, ...patchPendiente() } as Record<string, unknown>;
  // Algo puesto en el panel plegado (guardado o en el borrador): sigue aplicándose.
  const enUso =
    !!siguiente.mediaConsentText ||
    siguiente.mediaAdoptGeneric === true ||
    siguiente.smsMarketingEnabled === true ||
    siguiente.standardTier === true;
  const si = t('common.yes');
  const no = t('common.no');
  const docTerms = siguiente.termsText
    ? t('settings.legal.ack.ownText')
    : siguiente.termsUrl
      ? t('settings.legal.ack.ownUrl')
      : siguiente.standardTier
        ? t('settings.legal.ack.standard')
        : t('settings.legal.ack.none');
  const resumen = [
    { label: t('settings.legal.terms.title'), value: docTerms },
    {
      label: t('settings.legal.privacy.title'),
      value: siguiente.privacyUrl
        ? t('settings.legal.ack.ownUrl')
        : siguiente.standardTier
          ? t('settings.legal.ack.standard')
          : t('settings.legal.ack.none'),
    },
    {
      label: t('settings.legal.rules.title'),
      value: siguiente.officialRulesUrl || siguiente.officialRulesText ? t('settings.legal.ack.own') : t('settings.legal.ack.none'),
    },
    {
      label: t('settings.legal.media.title'),
      value: siguiente.mediaConsentText
        ? t('settings.legal.ack.own')
        : siguiente.mediaAdoptGeneric
          ? t('settings.legal.ack.generic')
          : t('settings.legal.ack.none'),
    },
    { label: t('settings.legal.marketing.emailLabel'), value: siguiente.emailMarketingEnabled ? si : no },
    { label: t('settings.legal.marketing.smsLabel'), value: siguiente.smsMarketingEnabled ? si : no },
    {
      label: t('settings.legal.ageGate.title'),
      value: siguiente.ageGateEnabled ? `${si} · ${String(siguiente.ageGateMinAge ?? 21)}` : no,
    },
    { label: t('settings.legal.standard.title'), value: siguiente.standardTier ? si : no },
  ];

  return (
    <div className="space-y-6">
      <AcknowledgmentDialog
        open={firmando}
        onOpenChange={setFirmando}
        documentType="legal_setup_ack"
        brandId={companyId}
        brandLegalName={(siguiente.legalName as string | null) || datos.brandName}
        subject={datos.brandName}
        summary={resumen}
        payload={{ legal: siguiente, brandName: datos.brandName }}
        onAcknowledged={(ack) => guardar.mutate(ack)}
      />
      {datos.source === 'legacy' ? (
        <Aviso icon={<Info className="h-4 w-4" />}>{t('settings.legal.legacyNotice')}</Aviso>
      ) : null}
      {!puedeEditar ? <Aviso icon={<Info className="h-4 w-4" />}>{t('settings.legal.readOnly')}</Aviso> : null}

      {/* Razón social */}
      <Seccion
        icon={<FileText className="h-4 w-4" />}
        title={t('settings.legal.legalName.title')}
        tag={t('settings.legal.tag.brand')}
        description={t('settings.legal.legalName.description')}
      >
        <Input
          value={texto('legalName')}
          onChange={(e) => tocar('legalName', e.target.value)}
          placeholder={t('settings.legal.legalName.placeholder')}
          disabled={!puedeEditar}
          className="max-w-md"
        />
      </Seccion>

      {/* Términos */}
      <Seccion
        icon={<Link2 className="h-4 w-4" />}
        title={t('settings.legal.terms.title')}
        tag={t('settings.legal.tag.brandLink')}
        description={t('settings.legal.terms.description')}
      >
        <Campo label={t('settings.legal.field.url')} hint={t('settings.legal.terms.urlHint')}>
          <Input
            type="url"
            value={texto('termsUrl')}
            onChange={(e) => tocar('termsUrl', e.target.value)}
            placeholder="https://"
            disabled={!puedeEditar}
          />
        </Campo>
        <CampoTexto
          label={t('settings.legal.field.text')}
          hint={t('settings.legal.terms.textHint')}
          value={texto('termsText')}
          onChange={(v) => tocar('termsText', v)}
          placeholder={t('settings.terms.general.placeholder')}
          disabled={!puedeEditar}
        />
      </Seccion>

      {/* Privacidad */}
      <Seccion
        icon={<Link2 className="h-4 w-4" />}
        title={t('settings.legal.privacy.title')}
        tag={t('settings.legal.tag.brandLink')}
        description={t('settings.legal.privacy.description')}
      >
        <Campo label={t('settings.legal.field.url')} hint={t('settings.legal.privacy.urlHintPanel')}>
          <Input
            type="url"
            value={texto('privacyUrl')}
            onChange={(e) => tocar('privacyUrl', e.target.value)}
            placeholder="https://"
            disabled={!puedeEditar}
          />
        </Campo>
      </Seccion>

      {/* Official Rules */}
      <Seccion
        icon={<Trophy className="h-4 w-4" />}
        title={t('settings.legal.rules.title')}
        tag={t('settings.legal.tag.ifPrize')}
        description={t('settings.legal.rules.description')}
      >
        <Campo label={t('settings.legal.field.url')} hint={t('settings.legal.rules.urlHint')}>
          <Input
            type="url"
            value={texto('officialRulesUrl')}
            onChange={(e) => tocar('officialRulesUrl', e.target.value)}
            placeholder="https://"
            disabled={!puedeEditar}
          />
        </Campo>
        <CampoTexto
          label={t('settings.legal.field.text')}
          hint={t('settings.legal.rules.textHint')}
          value={texto('officialRulesText')}
          onChange={(v) => tocar('officialRulesText', v)}
          placeholder={t('settings.legal.rules.placeholder')}
          disabled={!puedeEditar}
        />
      </Seccion>

      {/* Age gate */}
      <Seccion
        icon={<ShieldCheck className="h-4 w-4" />}
        title={t('settings.legal.ageGate.title')}
        tag={t('settings.legal.tag.config')}
        description={t('settings.legal.ageGate.description')}
      >
        <Switch
          checked={ageGate}
          onChange={(v) => {
            tocar('ageGateEnabled', v);
            if (v && valor('ageGateMinAge') == null) tocar('ageGateMinAge', 21);
          }}
          label={t('settings.legal.ageGate.requireLabel')}
          description={t('settings.legal.ageGate.requireDescription')}
          disabled={!puedeEditar}
        />
        <Campo label={t('settings.legal.ageGate.minAge')} hint={t('settings.legal.ageGate.minAgeHint')}>
          <Select
            value={edad}
            onChange={(e) => tocar('ageGateMinAge', Number(e.target.value))}
            disabled={!puedeEditar || !ageGate}
            className="max-w-[10rem]"
          >
            <option value={18}>18</option>
            <option value={21}>21</option>
          </Select>
        </Campo>
      </Seccion>

      {/* Marketing por email */}
      <Seccion
        icon={<Mail className="h-4 w-4" />}
        title={t('settings.legal.marketing.emailTitle')}
        tag={t('settings.legal.tag.config')}
        description={t('settings.legal.marketing.emailOnlyDescription')}
      >
        <Switch
          checked={bool('emailMarketingEnabled')}
          onChange={(v) => tocar('emailMarketingEnabled', v)}
          label={t('settings.legal.marketing.emailLabel')}
          description={t('settings.legal.marketing.emailDescription')}
          disabled={!puedeEditar}
        />
      </Seccion>

      {/* Otras aplicaciones y eventos anteriores: se pliega, no se quita (único editor de co_company_legal). */}
      <Card className="p-5">
        <Collapsible
          open={otrasAbiertas}
          onOpenChange={setOtrasAbiertas}
          label={
            <span className="flex items-center gap-2 text-base font-semibold text-[var(--color-fg-primary)]">
              {t('settings.legal.other.title')}
              {enUso ? <Badge tone="neutral">{t('settings.legal.other.inUse')}</Badge> : null}
            </span>
          }
        >
          <div className="grid gap-4">
            <p className="text-sm text-[var(--color-fg-muted)]">{t('settings.legal.other.description')}</p>
            <Seccion
              icon={<Camera className="h-4 w-4" />}
              title={t('settings.legal.media.title')}
              tag={t('settings.legal.tag.ifPhotos')}
              description={t('settings.legal.media.description')}
            >
              <CampoTexto
                label={t('settings.legal.field.text')}
                hint={t('settings.legal.media.textHint')}
                value={texto('mediaConsentText')}
                onChange={(v) => tocar('mediaConsentText', v)}
                placeholder={t('settings.terms.photo.placeholder')}
                disabled={!puedeEditar}
              />
              <Switch
                checked={bool('mediaAdoptGeneric')}
                onChange={(v) => tocar('mediaAdoptGeneric', v)}
                label={t('settings.legal.media.adoptLabel')}
                description={t('settings.legal.media.adoptDescription')}
                disabled={!puedeEditar}
              />
            </Seccion>
            <Seccion
              icon={<Mail className="h-4 w-4" />}
              title={t('settings.legal.sms.title')}
              tag={t('settings.legal.tag.config')}
              description={t('settings.legal.sms.description')}
            >
              <Switch
                checked={bool('smsMarketingEnabled')}
                onChange={(v) => tocar('smsMarketingEnabled', v)}
                label={t('settings.legal.marketing.smsLabel')}
                description={t('settings.legal.marketing.smsDescription')}
                disabled={!puedeEditar}
              />
            </Seccion>
            <Seccion
              icon={<Scale className="h-4 w-4" />}
              title={t('settings.legal.standard.title')}
              tag={t('settings.legal.tag.onxite')}
              description={t('settings.legal.standard.description')}
            >
              <Switch
                checked={bool('standardTier')}
                onChange={(v) => tocar('standardTier', v)}
                label={t('settings.legal.standard.label')}
                description={t('settings.legal.standard.labelDescription')}
                disabled={!puedeEditar}
              />
            </Seccion>
          </div>
        </Collapsible>
      </Card>

      <div className="flex items-center justify-between gap-3">
        <p className="text-xs text-[var(--color-fg-muted)]">
          {datos.updatedAt
            ? t('settings.legal.lastSaved', { when: new Date(datos.updatedAt).toLocaleString() })
            : t('settings.terms.empty')}
          {' · '}
          <Link to="/settings/brand" className="underline">
            {t('settings.legal.toBrandSettings')}
          </Link>
        </p>
        <div className="flex flex-wrap items-center justify-end gap-3">
          {excede ? (
            <p role="status" className="text-xs text-[var(--color-error)]">
              {t('settings.legal.textLimit.saveBlocked', { max: formato(LEGAL_TEXT_MAX_LENGTH) })}
            </p>
          ) : null}
          {/* Con un texto tocado por encima del tope no se abre el acuse: no se firma lo que el servidor rechazaría. */}
          <Button
            onClick={() => setFirmando(true)}
            loading={guardar.isPending}
            disabled={!sucio || !puedeEditar || excede}
          >
            {t('settings.legal.save')}
          </Button>
        </div>
      </div>

      <Historial companyId={companyId} />
    </div>
  );
}

/**
 * Los reconocimientos de la marca (Exhibit F): quién firmó qué y cuándo. Es la
 * parte «vendible» del registro — la marca puede sacar su propio historial de
 * cumplimiento sin pedírselo a Onxite.
 */
function Historial({ companyId }: { companyId: number }) {
  const t = useT();
  const q = useQuery({
    queryKey: ['legal', 'brand', companyId],
    queryFn: () => legalApi.forBrand(companyId),
  });
  const filas = (q.data ?? []).filter((a) =>
    ['legal_setup_ack', 'data_export_ack', 'media_adopt_generic_ack', 'standard_tier_ack', 'dpa', 'data_controller_ack'].includes(
      a.documentType,
    ),
  );
  return (
    <Card className="p-5">
      <div className="flex items-center gap-2">
        <ShieldCheck className="h-4 w-4 text-[var(--color-accent-ink)]" />
        <h2 className="text-base font-semibold text-[var(--color-fg-primary)]">{t('settings.legal.history.title')}</h2>
      </div>
      <p className="mt-1 text-sm text-[var(--color-fg-muted)]">{t('settings.legal.history.description')}</p>
      {q.isLoading ? (
        <Skeleton className="mt-4 h-16 w-full" />
      ) : filas.length === 0 ? (
        <p className="mt-4 text-sm text-[var(--color-fg-muted)]">{t('settings.legal.history.empty')}</p>
      ) : (
        <ul className="mt-4 divide-y divide-[var(--color-border-subtle)]">
          {filas.map((a) => (
            <li key={a.id} className="flex flex-wrap items-baseline justify-between gap-2 py-2 text-sm">
              <span className="font-medium text-[var(--color-fg-primary)]">
                {t(`settings.legal.history.type.${a.documentType}`)}
                <span className="ml-2 text-xs text-[var(--color-fg-muted)]">{a.documentVersion}</span>
              </span>
              <span className="text-xs text-[var(--color-fg-muted)]">
                {[a.signerName, a.signerTitle].filter(Boolean).join(' · ')}
                {a.signerName ? ' · ' : ''}
                {new Date(a.acceptedAt).toLocaleString()}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function Seccion({
  icon,
  title,
  tag,
  description,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  tag: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <Card className="p-5">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[var(--color-accent-ink)]">{icon}</span>
        <h2 className="text-base font-semibold text-[var(--color-fg-primary)]">{title}</h2>
        <Badge tone="neutral">{tag}</Badge>
      </div>
      <p className="mt-1 text-sm text-[var(--color-fg-muted)]">{description}</p>
      <div className="mt-4 grid gap-4">{children}</div>
    </Card>
  );
}

function Campo({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-[11px] font-extrabold uppercase tracking-widest text-[var(--color-fg-muted)]">
        {label}
      </span>
      {hint ? <p className="mb-2 mt-1 text-xs text-[var(--color-fg-muted)]">{hint}</p> : null}
      {children}
    </label>
  );
}

/**
 * Un documento legal pegado: etiqueta, para qué sirve, contador y el área.
 * Por debajo del 90 % del tope el contador es el de siempre; desde ahí,
 * «n / tope»; por encima, en rojo y con un aviso bajo el área. Sin `maxLength`
 * nativo: cortaría en silencio un documento legal pegado.
 */
function CampoTexto({
  label,
  hint,
  value,
  onChange,
  placeholder,
  disabled,
}: {
  label: string;
  hint: string;
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  disabled?: boolean;
}) {
  const t = useT();
  const language = usePrefsStore((s) => s.language);
  const idAviso = useId();
  const largo = value.length;
  const pasado = largo > LEGAL_TEXT_MAX_LENGTH;
  const numero = new Intl.NumberFormat(language);
  const max = numero.format(LEGAL_TEXT_MAX_LENGTH);
  return (
    <label className="block">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="text-[11px] font-extrabold uppercase tracking-widest text-[var(--color-fg-muted)]">
          {label}
        </span>
        <span
          className={cn(
            'text-[11px]',
            pasado ? 'font-semibold text-[var(--color-error)]' : 'text-[var(--color-fg-muted)]',
          )}
        >
          {largo >= LEGAL_TEXT_WARN_AT
            ? t('settings.legal.textLimit.counter', { n: numero.format(largo), max })
            : value.trim()
              ? t('settings.terms.chars', { n: String(value.trim().length) })
              : t('settings.terms.empty')}
        </span>
      </div>
      <p className="mb-2 mt-1 text-xs text-[var(--color-fg-muted)]">{hint}</p>
      <Textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={7}
        disabled={disabled}
        invalid={pasado}
        aria-invalid={pasado ? true : undefined}
        aria-describedby={pasado ? idAviso : undefined}
        className="resize-y font-mono text-xs leading-relaxed"
      />
      {pasado ? (
        <p id={idAviso} className="mt-1 text-xs text-[var(--color-error)]">
          {t('settings.legal.textLimit.over', { max })}
        </p>
      ) : null}
    </label>
  );
}

function Aviso({ icon, children }: { icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-2 rounded-lg border border-dashed border-[var(--color-border-default)] bg-[var(--color-bg-elevated)] px-3 py-2 text-xs text-[var(--color-fg-muted)]">
      <span className="mt-0.5 shrink-0">{icon}</span>
      <span>{children}</span>
    </div>
  );
}
